import ReactDOM from 'react-dom';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import styled, { keyframes } from 'styled-components/macro';
import { useTranslation } from 'react-i18next';
import { getBasket_basket } from '../HeaderBasket/__generated__/getBasket';
import { BasketBarSelectedProducts_selectedProducts } from './__generated__/BasketBarSelectedProducts';
import BasketBarSale from './BasketBarSale';
import BasketBarProduct from './BasketBarProduct';
import BasketBarOrderSummary from './BasketBarOrderSummary';
import BasketBarReassuranceSlider from './BasketBarReassuranceSlider';
import BasketBarCrossSell from './BasketBarCrossSell';
import BasketBarCrossSellPicker from './BasketBarCrossSellPicker';
import { IconClose } from 'Ui/Icons';
import AuthStore from 'Store/AuthStore';
import showLoginModal from 'Components/modals/LoginRegister';
import { allowedTypes } from 'Utils/memberTypes';
import { gtmUpdateItemQttyItem, gtmViewCart } from 'Libs/GA4/common';
import { pushRtbhBasketStatus, pushRtbhStartOrder } from 'Libs/RtbHouse/events';

/* ─────────────────────────── Queries ─────────────────────────── */

const CODE_PROMO_MESSAGE_TUNNEL = gql`
  query BasketBarPromoCodeTunnel {
    promoCodeProgression {
      message
      progression
    }
  }
`;

const ADD_PRODUCT = gql`
  mutation BasketBarAddProduct($productId: ID!, $quantity: Int!, $saleId: ID!) {
    addProductToBasket(
      productId: $productId
      quantity: $quantity
      saleId: $saleId
    ) {
      id
      productCount
      totalPrice
      totalSaving
      totalPricePlusShipping
      basketPromoCode {
        code
        value
      }
      basketSales {
        basketProducts {
          basketProductId
          productId
          price
          totalPrice
          totalEcoTax
          totalPublicPrice
          productCount
          quantityMax
          size
          product {
            id
            groupId
            title
            publicPrice
            sellPrice
            appliedPrice
            ecoParticipation
            discountPercentage
            brandDatas {
              name
            }
            sizeDatas {
              productId
              size
              stock
              maxQuantity
              sellPrice
              publicPrice
            }
            imagesDatas {
              urlListPage
            }
          }
        }
        sale {
          id
          name
          minimumDeliveryDate
          maximumDeliveryDate
          productDeliveryDate
          promoMulti
          promoCategory
          saleArea
        }
      }
    }
  }
`;

/* ─────────────────────────── Animations ─────────────────────────── */

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
`;

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to   { transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 0.5; }
`;

/* ─────────────────────────── Styled ─────────────────────────── */

const Overlay = styled.div`
  background: #000;
  opacity: 0.5;
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 19999;
  animation: ${fadeIn} 0.2s ease forwards;
`;

const SidebarWrapper = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 37.5rem;
  height: 100vh;
  z-index: 20000;
  overflow: hidden;
  animation: ${slideIn} 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.12);

  @media screen and (max-width: 48rem) {
    width: 100%;
    top: auto;
    bottom: 0;
    left: 0;
    height: auto;
    border-radius: 16px 16px 0 0;
    animation: ${slideUp} 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
`;

const SideNav = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
  background-color: #fff;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media screen and (max-width: 48rem) {
    height: auto;
    max-height: 92dvh;
    overflow-y: auto;
  }
`;

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const HeadContainer = styled.div`
  display: flex;
  box-shadow: inset 0px 3px 9px -4px rgba(0, 0, 0, 0.1);
  justify-content: space-between;
  align-items: center;
  position: sticky;
  width: 100%;
  box-sizing: border-box;
  top: 0;
  z-index: 1000;
  background-color: #fff;
  padding: 1.6rem;

  svg path {
    stroke-width: 1px !important;
  }
`;

const MobileHandle = styled.div`
  display: none;

  @media screen and (max-width: 48rem) {
    display: block;
    width: 3.6rem;
    height: 4px;
    background: #e0e0e0;
    border-radius: 2px;
    margin: 1.2rem auto 0;
  }
`;

const Title = styled.div`
  color: #000;
  font-family: var(--font-primary);
  font-size: 1.4rem;
  font-weight: 600;
  text-transform: uppercase;
`;

const MenuContainer = styled.div`
  padding: 0 1.6rem;
`;

/* ── Progress bar ── */

const ProgressBarContainer = styled.div`
  padding: 1.2rem 1.6rem;
  margin: 1.2rem 1.6rem 1.6rem;
  background-color: #f5f5f5;
  border-radius: 8px;
  box-sizing: border-box;
`;

const ProgressBarMessage = styled.p`
  font-family: var(--font-primary);
  font-size: 1.6rem;
  font-weight: 500;
  color: #000;
  margin-bottom: 0.6rem;

  b,
  strong {
    font-weight: 700;
  }
  i,
  em {
    font-style: italic;
  }
  u {
    text-decoration: underline;
  }
`;

const ProgressBarTrack = styled.div`
  width: 100%;
  height: 5px;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div<{ $value: number }>`
  height: 100%;
  width: ${({ $value }) => Math.min($value, 100)}%;
  background-color: #000;
  border-radius: 3px;
  transition: width 0.4s ease;
`;

/* ── Footer ── */

const ContainerButton = styled.div`
  position: sticky;
  width: 100%;
  bottom: 0;
  z-index: 1000;
  background: #fff;
  box-shadow: 0px 0px 15px 0px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  border-top: 1px solid #e8e8e8;
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.2rem 1.6rem 1.6rem;
`;

const CheckoutButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 1.6rem;
  background: var(--design-base-color, #000);
  color: #fff;
  font-family: var(--font-primary);
  font-size: 1.4rem;
  font-weight: 700;
  text-transform: uppercase;
  text-decoration: none;
  border-radius: 25px;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
    color: #fff;
  }
`;

const CrossSellWrapper = styled.div`
  border-top: 1px solid #e8e8e8;
  margin-top: 1.6rem;
`;

/* ─────────────────────────── Main BasketBar ─────────────────────────── */

interface Props {
  basket: getBasket_basket;
  onClose: () => void;
  onBasketUpdate: (updated: getBasket_basket) => void;
}

function BasketBarContent({ basket, onClose, onBasketUpdate }: Props) {
  const { t } = useTranslation('shipping', { useSuspense: false });
  const isLoggedIn = AuthStore.useStoreState((state) => state.isLoggedIn);
  const sessionMemberType = AuthStore.useStoreState(
    (state) => state.sessionMemberType
  );
  const member = AuthStore.useStoreState((state) => state.member);

  // RTB House — statut du panier (productGroup présents), à l'ouverture du
  // panier et à chaque changement de composition.
  const basketGroupIds = basket.basketSales
    .flatMap((s) => (s?.basketProducts ?? []).map((bp) => bp?.product?.groupId))
    .filter((id): id is number => Boolean(id));
  const basketGroupIdsKey = basketGroupIds.join(',');

  useEffect(() => {
    if (basketGroupIds.length > 0) {
      pushRtbhBasketStatus(basketGroupIds, member);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basketGroupIdsKey, member?.emailHash]);

  // GA4 view_cart : déclenché une seule fois à l'ouverture (affichage) de la
  // sidebar panier.
  const viewCartSentRef = useRef(false);
  useEffect(() => {
    if (viewCartSentRef.current) return;
    viewCartSentRef.current = true;

    const items = basket.basketSales.flatMap((basketSale) => {
      const sale = basketSale?.sale;
      return (basketSale?.basketProducts ?? [])
        .filter((bp): bp is NonNullable<typeof bp> => Boolean(bp))
        .map((bp) => ({
          item_id: bp.product.groupId,
          item_name: bp.product?.title,
          item_brand: bp.product?.brandDatas?.name,
          item_category: sale?.saleArea ?? '',
          item_delivery_date: sale?.productDeliveryDate,
          price: bp.price,
          quantity: bp.productCount,
          currency: 'EUR',
          discount: `${bp.product?.discountPercentage}%`,
          promotion_id: sale?.id,
          promotion_name: sale?.name,
        }));
    });

    gtmViewCart({
      value: basket.totalPrice,
      coupon: basket.basketPromoCode?.code ?? '',
      items,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { data: promoData } = useQuery(CODE_PROMO_MESSAGE_TUNNEL, {
    fetchPolicy: 'network-only',
  });
  const promoMessage: string | undefined =
    promoData?.promoCodeProgression?.message;
  const promoProgression: number | undefined =
    promoData?.promoCodeProgression?.progression;

  /* ── CrossSell picker state (lifted here so picker renders outside SideNav) ── */
  const [pickerProduct, setPickerProduct] =
    useState<BasketBarSelectedProducts_selectedProducts | null>(null);
  const [selectedSize, setSelectedSize] = useState('');
  const [addingGroupId, setAddingGroupId] = useState<number | null>(null);

  const [addProductMutation] = useMutation(ADD_PRODUCT);

  const handleAddDirect = async (
    product: BasketBarSelectedProducts_selectedProducts,
    productId: string
  ) => {
    if (!product.saleId) return;
    setAddingGroupId(product.groupId);
    try {
      const { data } = await addProductMutation({
        variables: {
          productId,
          quantity: 1,
          saleId: product.saleId.toString(),
        },
      });
      if (data?.addProductToBasket) {
        onBasketUpdate(data.addProductToBasket);
        gtmUpdateItemQttyItem({
          event: 'add_to_cart',
          ecommerce: {
            items: [
              {
                item_id: product.id,
                item_name: product.title,
                item_brand: product.brandDatas?.name,
                item_list_name: 'basket_cross_sell',
                price: product.sellPrice,
                quantity: 1,
                currency: 'EUR',
                discount: `${product.discountPercentage}%`,
                promotion_id: product.saleId,
              },
            ],
          },
        });
      }
    } finally {
      setAddingGroupId(null);
    }
  };

  const handlePickerOpen = (
    product: BasketBarSelectedProducts_selectedProducts
  ) => {
    const available = (product.sizeDatas ?? []).filter(
      (s) => (s.stock ?? 0) > 0
    );
    if (available.length === 0) return;
    /* Toujours afficher le picker (même taille unique, pré-sélectionnée) */
    setSelectedSize(available.length === 1 ? available[0].size : '');
    setPickerProduct(product);
  };

  const handlePickerConfirm = async () => {
    if (!pickerProduct || !selectedSize) return;
    const sizeData = (pickerProduct.sizeDatas ?? []).find(
      (s) => s.size === selectedSize
    );
    if (!sizeData) return;
    setPickerProduct(null);
    await handleAddDirect(pickerProduct, sizeData.productId.toString());
  };

  const handleUpdate = (updatedBasket: any) => {
    onBasketUpdate(updatedBasket);
    if (updatedBasket.productCount === 0) onClose();
  };

  return (
    <>
      <Overlay onClick={onClose} />

      <SidebarWrapper>
        <SideNav>
          <MobileHandle />
          <MainContainer>
            <HeadContainer>
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Title>
                  <span style={{ fontWeight: 600 }}>{t('basketBarTitle')}</span>{' '}
                  ({basket.productCount}{' '}
                  {basket.productCount > 1
                    ? t('articlePlural')
                    : t('articleSingular')}
                  )
                </Title>
                <IconClose
                  onClick={onClose}
                  style={{ cursor: 'pointer', strokeWidth: '0.5px' }}
                  width={20}
                  height={20}
                />
              </div>
            </HeadContainer>

            {promoMessage && promoProgression !== undefined && (
              <ProgressBarContainer>
                <ProgressBarMessage
                  dangerouslySetInnerHTML={{ __html: promoMessage }}
                />
                <ProgressBarTrack>
                  <ProgressBarFill $value={Number(promoProgression)} />
                </ProgressBarTrack>
              </ProgressBarContainer>
            )}

            <MenuContainer>
              {basket.basketSales.map((basketSale, saleIndex) => {
                if (!basketSale?.sale) return null;
                const products = (basketSale.basketProducts || []).filter(
                  Boolean
                );
                if (!products.length) return null;

                return (
                  <div
                    key={basketSale.sale.id ?? saleIndex}
                    style={{ marginBottom: '8px' }}
                  >
                    <BasketBarSale sale={basketSale.sale} />
                    {products.map((product) => {
                      if (!product) return null;
                      return (
                        <BasketBarProduct
                          key={product.basketProductId}
                          productInformation={product}
                          sale={basketSale.sale!}
                          onBasketUpdate={handleUpdate}
                        />
                      );
                    })}
                  </div>
                );
              })}
            </MenuContainer>

            <CrossSellWrapper>
              <BasketBarCrossSell
                onPickerOpen={handlePickerOpen}
                addingGroupId={addingGroupId}
              />
            </CrossSellWrapper>
          </MainContainer>

          <ContainerButton>
            <BasketBarOrderSummary basket={basket} />
            <BottomSection>
              {allowedTypes.includes(sessionMemberType) ? (
                <CheckoutButton
                  style={{ width: 'auto !important', color: '#FFF !important' }}
                  href="/v3/checkout"
                  onClick={() => pushRtbhStartOrder(member)}
                >
                  {t('checkoutButtonDelivery')}
                </CheckoutButton>
              ) : (
                <CheckoutButton
                  as="button"
                  style={{ width: 'auto !important', color: '#FFF !important' }}
                  onClick={() => {
                    pushRtbhStartOrder(member);
                    showLoginModal({
                      linkRedirect: '/v3/checkout',
                      isRegister: true,
                    });
                  }}
                >
                  {t('checkoutButtonLogin')}
                </CheckoutButton>
              )}
              <BasketBarReassuranceSlider isAlma={!!basket.isAlma} />
            </BottomSection>
          </ContainerButton>
        </SideNav>

        <BasketBarCrossSellPicker
          product={pickerProduct}
          selectedSize={selectedSize}
          onSelectSize={setSelectedSize}
          onClose={() => setPickerProduct(null)}
          onConfirm={handlePickerConfirm}
          isAdding={addingGroupId !== null}
        />
      </SidebarWrapper>
    </>
  );
}

export const BasketBar = (props: Props) =>
  ReactDOM.createPortal(<BasketBarContent {...props} />, document.body);
